import { H3, InputField } from 'base-components';
import { px2rem } from 'decisiv-ui-utils';
import { debounce, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Trans, withI18n } from 'lingui-react';
import { compose, setDisplayName } from 'recompose';

import {
  Container,
  Row,
  Column,
} from 'styled-components-reactive-grid';

import withGeocoder from 'utils/mapping/withGeocoder';

import ServiceProvidersList from 'components/ServiceProvidersList';
import ServiceRequestMap from 'components/ServiceRequestMap';
import WizardSearchError from 'components/WizardSearchError';

import InputWrapper from './InputWrapper';
import ResultWrapper from './ResultWrapper';

import withConnectedData from './Container';
import messages from './messages';

function locationToString({ lat, lon }) {
  return lat && lon ? `${lat}, ${lon}` : '';
}

const DEFAULT_SEARCH_RADIUS = 50;

function findServiceProviders(props, location) {
  const origin = locationToString(location);
  if (origin) {
    props.clearServiceProviders();
    props.loadServiceProviders({
      origin,
      radius: DEFAULT_SEARCH_RADIUS,
    });
  }
}

// Note we had to send `this` as a parameter due to when...
//   - Calling any method from a constructor,
//   - And you have an unrelated async fat arrow function in the class (that is not the method
//     being called in the constructor)
// ...the async fat arrow function looses `this`.
//
// This appears to be an issue with Babel that has been squashed a few times but
// continues to resurface.

export async function handleGeocode(self) {
  const address = self.state.searchValue;

  self.setState({ currentLocation: {} });
  try {
    const props = self.props;
    const { location, count } = await props.geocode(address);

    if (!count) {
      self.setGeocodeError();
    } else if (count === 1) {
      findServiceProviders(props, location);
      self.setState({ currentLocation: location });
    } else {
      self.setGeocodeError();
    }
  } catch (status) {
    self.setGeocodeError();
  }
}

/**
 * Given input data from a previous pass through this step, extract the `currentLocation`
 * object containing the lat/lon values.
 * @param inputData
 * @returns {*}
 */
export function getCurrentLocation({
  inputData: { assetCurrentLocation: prevLocation, asset } = {},
}) {
  if (prevLocation) {
    return prevLocation;
  }

  const assetEmbeddedLocation = asset && asset.location;

  return assetEmbeddedLocation || {};
}

export function getSearchValue({ inputData }) {
  if (inputData && inputData.assetLocationSearchValue) {
    return inputData.assetLocationSearchValue;
  }
  return locationToString(getCurrentLocation({ inputData }));
}

/**
 * The ServiceProviderSelectionStep has an input for specifying the location of the
 * asset (or some other location to use as the center for the provider search). This
 * input is stored in state as `searchValue`.
 *
 * In response to a change to this input value, a geocode is performed, and the resulting
 * location (an object with lat/lon) is stored in state as `currentLocation`.  Also, the
 * resulting location from the geocode is turned into a query for the `loadServiceProviders`
 * API call, which is received as a prop.
 *
 * The resulting provider list from `loadServiceProviders` is received as a `serviceProviders`
 * prop, and passed down to the `ServiceProvidersList` component.
 */
export class ServiceProviderSelectionStep extends Component {
  static propTypes = {
    // provided by the wizard controller to pass up this step's data:
    completeStep: PropTypes.func.isRequired,

    // previously-collected input data from the wizard controller:
    inputData: PropTypes.shape({
      asset: PropTypes.shape({
        location: PropTypes.shape({
          lat: PropTypes.string,
          lon: PropTypes.string,
        }),
      }),
      serviceProvider: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),

    // API action to look up service providers:
    // eslint-disable-next-line react/no-unused-prop-types
    loadServiceProviders: PropTypes.func.isRequired,

    // service providers from the API lookup:
    serviceProviders: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      }),
    ).isRequired,

    // true when the service provider API lookup is in flight:
    serviceProvidersPending: PropTypes.bool.isRequired,

    // clear any service providers from a previous API lookup:
    clearServiceProviders: PropTypes.func.isRequired,

    // async geocoding function provided by withGeocoder:
    // eslint-disable-next-line react/no-unused-prop-types
    geocode: PropTypes.func.isRequired,

    i18n: PropTypes.shape({
      _: PropTypes.func.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    inputData: {},
  };

  state = {
    currentLocation: getCurrentLocation(this.props),
    searchValue: getSearchValue(this.props),
    searchErrorSubTitle: messages.suggestion,
    isSearching: false,
  };

  componentWillMount() {
    const { currentLocation } = this.state;
    if (currentLocation) {
      findServiceProviders(this.props, currentLocation);
    }
  }

  componentWillReceiveProps(nextProps) {
    const {
      currentLocation,
      searchValue,
    } = this.state;
    const { serviceProviders, serviceProvidersPending } = nextProps;
    if (currentLocation && isEmpty(serviceProviders) && !serviceProvidersPending) {
      const searchErrorSubTitle = searchValue === '' ?
        messages.suggestion :
        messages.noAssetLocationSubtitle;
      this.setState({
        searchErrorSubTitle,
        isSearching: false,
      });
    }
  }

  setGeocodeError = () => {
    this.props.clearServiceProviders();
    const searchErrorSubTitle = this.state.searchValue === '' ?
      messages.suggestion :
      messages.noAssetLocationSubtitle;
    this.setState({
      searchErrorSubTitle,
      currentLocation: {},
      isSearching: false,
    });
  };

  handleAssetLocationInputChange = (e) => {
    this.setState({ searchValue: e.target.value });
    if (!this.state.isSearching) {
      this.setState({ isSearching: true });
    }
    this.debounceGeoCode();
  };

  debounceGeoCode = debounce(() => handleGeocode(this), 1000);

  submitInputData = (selectedServiceProvider) => {
    const stepData = {
      assetCurrentLocation: this.state.currentLocation,
      assetLocationSearchValue: this.state.searchValue,
      serviceProvider: selectedServiceProvider,
    };
    this.props.completeStep(stepData);
  };

  render() {
    const {
      searchErrorSubTitle,
      searchValue,
      isSearching,
      currentLocation,
    } = this.state;
    const {
      serviceProviders,
      inputData: { serviceProvider },
      i18n,
    } = this.props;
    return (
      <Container style={{ padding: `${px2rem(30)} 0` }}>
        <Row>
          <Column modifiers={['col_offset_3', 'col_6']}>
            <H3 modifiers={['fontWeightRegular']}>
              <Trans id={messages.title} />
            </H3>
          </Column>
        </Row>
        <Row style={{ marginTop: `${px2rem(30)}` }}>
          <Column modifiers={['col_offset_1', 'col_10']}>
            <Row>
              <Column modifiers={['col']}>
                <ServiceRequestMap
                  serviceProviders={serviceProviders}
                  currentLocation={currentLocation}
                />
              </Column>
              <Column
                modifiers={['col']}
                style={{ height: `${px2rem(400)}`, marginLeft: `${px2rem(20)}` }}
              >
                <InputWrapper>
                  <InputField
                    name="search"
                    onChange={this.handleAssetLocationInputChange}
                    onBlur={this.handleAssetLocationInputChange}
                    placeholder={i18n._(messages.placeholder)}
                    value={searchValue}
                  >
                    <Column modifiers={['col', 'padScaleY_0']}>
                      <Row>
                        <InputField.Label>
                          <Trans id={messages.assetLocation} />
                        </InputField.Label>
                      </Row>
                      <Row>
                        <InputField.TextField />
                      </Row>
                    </Column>
                  </InputField>
                  <ResultWrapper>
                    {serviceProviders.length > 0 &&
                      <ServiceProvidersList
                        onSelect={this.submitInputData}
                        selectedServiceProvider={serviceProvider}
                        serviceProviders={serviceProviders}
                      />
                    }
                    {!isSearching && serviceProviders.length === 0 &&
                      <WizardSearchError
                        searchValue={searchValue}
                        titlePrefix={messages.noAssetLocationTitlePrefix}
                        titleSuffix={messages.titleSuffix}
                        subtitle={searchErrorSubTitle}
                      />
                    }
                  </ResultWrapper>
                </InputWrapper>
              </Column>
            </Row>
          </Column>
        </Row>
      </Container>
    );
  }
}

export default compose(
  setDisplayName('ServiceProviderSelectionStep'),
  withGeocoder,
  withConnectedData,
  withI18n(),
)(ServiceProviderSelectionStep);



// WEBPACK FOOTER //
// ./compositions/ServiceProviderSelectionStep/index.js